/** Only this allowlisted, identifier-free model is passed to the homepage UI. */
export type NodeKind = 'company' | 'person' | 'address' | 'vehicle' | 'phone' | 'email' | 'account' | 'case' | 'other';
export interface PreviewNode { key: number; kind: NodeKind; focus: boolean; }
export interface PreviewEdge { from: number; to: number; }
export interface PreviewGraph { nodes: PreviewNode[]; edges: PreviewEdge[]; }
export interface PositionedNode extends PreviewNode { x: number; y: number; }

export const graphNodeRadius = (count: number) => Math.max(7, Math.min(19, 115 / Math.sqrt(Math.max(1, count))));

export const nodeKinds: Record<NodeKind, { label: string; description: string }> = {
  company: { label: 'Company', description: 'Explore a business and the network around it.' },
  person: { label: 'Person', description: 'See how people connect companies through their engagements.' },
  address: { label: 'Address', description: 'Find businesses connected through a shared location.' },
  vehicle: { label: 'Vehicle', description: 'Put vehicle relationships into a company’s wider context.' },
  phone: { label: 'Phone', description: 'Follow shared contact points across a business network.' },
  email: { label: 'Email', description: 'Explore connections through shared email contact points.' },
  account: { label: 'Bank account', description: 'Explore account relationships within a network.' },
  case: { label: 'Case', description: 'Connect investigation context to the wider network.' },
  other: { label: 'Other entity', description: 'Explore another type of connected entity.' },
};

const object = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
const identifier = (value: unknown): string | null =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : null;
const kinds: Record<string, NodeKind> = {
  Company: 'company', FunktionarPerson: 'person', Person: 'person', Address: 'address',
  CarRegNumber: 'vehicle', PhoneNumber: 'phone', Email: 'email', BankAccount: 'account', CaseNumber: 'case',
};

/** Accepts graphData itself or the company-profile response containing graphData.
 * Names, source IDs, properties, metadata and risk details are discarded here.
 * Use a sanitized snapshot for publication: do not bundle a raw customer response.
 */
export function sanitizeGraph(payload: unknown): PreviewGraph {
  const root = object(payload);
  const firstProfile = Array.isArray(root.profiles) ? object(root.profiles[0]) : {};
  const graph = object(firstProfile.graphData ?? root.graphData ?? root);
  const rawNodes = Array.isArray(graph.nodes) ? graph.nodes : [];
  const rawEdges = Array.isArray(graph.relationships) ? graph.relationships : [];
  const lookup = new Map<string, number>();
  const nodes: PreviewNode[] = [];
  const source = identifier(object(graph.metadata).sourceNodeId);
  rawNodes.forEach(raw => {
    const node = object(raw);
    const id = identifier(node.id);
    if (id === null || lookup.has(id)) return;
    const key = nodes.length;
    lookup.set(id, key);
    const type = String(node.type);
    const kind = Object.prototype.hasOwnProperty.call(kinds, type) ? kinds[type] : 'other';
    nodes.push({ key, kind, focus: id === source });
  });
  if (!nodes.some(node => node.focus)) {
    const focus = nodes.find(node => node.kind === 'company') ?? nodes[0];
    if (focus) focus.focus = true;
  }
  const edges: PreviewEdge[] = [];
  rawEdges.forEach(raw => {
    const edge = object(raw);
    const from = lookup.get(identifier(edge.from) ?? '');
    const to = lookup.get(identifier(edge.to) ?? '');
    if (from === undefined || to === undefined || from === to) return;
    edges.push({ from, to });
  });
  return { nodes, edges };
}

/** Keep a connected neighborhood, balancing entity types at each distance from the focus. */
export function pruneGraph(graph: PreviewGraph, limit = 24): PreviewGraph {
  if (graph.nodes.length <= limit) return graph;
  const neighbors = graph.nodes.map(() => new Set<number>());
  graph.edges.forEach(({ from, to }) => {
    neighbors[from].add(to);
    neighbors[to].add(from);
  });
  const focus = graph.nodes.find(node => node.focus) ?? graph.nodes[0];
  const kept = new Set<number>([focus.key]);
  const counts = new Map<NodeKind, number>([[focus.kind, 1]]);
  let frontier = [focus.key];
  while (frontier.length && kept.size < limit) {
    const candidates = Array.from(new Set(frontier.flatMap(key => Array.from(neighbors[key]))))
      .filter(key => !kept.has(key));
    frontier = [];
    while (candidates.length && kept.size < limit) {
      candidates.sort((a, b) =>
        (counts.get(graph.nodes[a].kind) ?? 0) - (counts.get(graph.nodes[b].kind) ?? 0)
        || neighbors[b].size - neighbors[a].size || a - b);
      const key = candidates.shift()!;
      kept.add(key);
      frontier.push(key);
      const kind = graph.nodes[key].kind;
      counts.set(kind, (counts.get(kind) ?? 0) + 1);
    }
  }
  const lookup = new Map<number, number>();
  const nodes = graph.nodes.filter(node => kept.has(node.key)).map((node, key) => {
    lookup.set(node.key, key);
    return { ...node, key };
  });
  const edges = graph.edges.filter(edge => kept.has(edge.from) && kept.has(edge.to))
    .map(edge => ({ from: lookup.get(edge.from)!, to: lookup.get(edge.to)! }));
  return { nodes, edges };
}

/** A deterministic spring layout, independent of identifying fields. */
export function layoutGraph(graph: PreviewGraph): PositionedNode[] {
  const positions = graph.nodes.map((node, i) => {
    const angle = i * 2.399963;
    const radius = 60 + Math.sqrt(i) * 32;
    return { ...node, x: node.focus ? 300 : 300 + Math.cos(angle) * radius, y: node.focus ? 230 : 230 + Math.sin(angle) * radius };
  });
  const iterations = positions.length > 160 ? 100 : 550;
  for (let step = 0; step < iterations; step++) {
    const forces = positions.map(() => ({ x: 0, y: 0 }));
    for (let i = 0; i < positions.length; i++) {
      for (let j = i + 1; j < positions.length; j++) {
        const dx = positions[i].x - positions[j].x;
        const dy = positions[i].y - positions[j].y;
        const distance = Math.max(4, Math.hypot(dx, dy));
        const force = 3200 / (distance * distance);
        forces[i].x += dx / distance * force;
        forces[i].y += dy / distance * force;
        forces[j].x -= dx / distance * force;
        forces[j].y -= dy / distance * force;
      }
    }
    graph.edges.forEach(edge => {
      const a = positions[edge.from];
      const b = positions[edge.to];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const distance = Math.max(1, Math.hypot(dx, dy));
      const force = (distance - 115) * 0.018;
      forces[a.key].x += dx / distance * force;
      forces[a.key].y += dy / distance * force;
      forces[b.key].x -= dx / distance * force;
      forces[b.key].y -= dy / distance * force;
    });
    positions.forEach((node, i) => {
      if (node.focus) return;
      node.x += Math.max(-5, Math.min(5, forces[i].x + (300 - node.x) * 0.001));
      node.y += Math.max(-5, Math.min(5, forces[i].y + (230 - node.y) * 0.001));
    });
  }
  if (!positions.length) return positions;
  const xs = positions.map(node => node.x);
  const ys = positions.map(node => node.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const scale = Math.min(480 / Math.max(1, maxX - minX), 335 / Math.max(1, maxY - minY));
  const fitted = positions.map(node => ({ ...node, x: 300 + (node.x - (minX + maxX) / 2) * scale, y: 240 + (node.y - (minY + maxY) / 2) * scale }));
  // Fitting can shrink gaps below the rendered circle sizes. Separate nodes in
  // the final SVG coordinates so the initial view has usable breathing room.
  const radius = graphNodeRadius(fitted.length);
  for (let step = 0; step < 200; step++) {
    let overlap = false;
    for (let i = 0; i < fitted.length; i++) {
      for (let j = i + 1; j < fitted.length; j++) {
        const a = fitted[i], b = fitted[j];
        const dx = b.x - a.x, dy = b.y - a.y;
        const distance = Math.hypot(dx, dy);
        const minimum = radius * 2 + (a.focus ? 8 : 0) + (b.focus ? 8 : 0) + 16;
        if (distance >= minimum - 0.01) continue;
        overlap = true;
        const shift = (minimum - distance) / 2;
        const ux = distance > 0 ? dx / distance : 1;
        const uy = distance > 0 ? dy / distance : 0;
        a.x -= ux * shift; a.y -= uy * shift;
        b.x += ux * shift; b.y += uy * shift;
      }
    }
    fitted.forEach(node => {
      node.x = Math.max(60, Math.min(540, node.x));
      node.y = Math.max(72, Math.min(408, node.y));
    });
    if (!overlap) break;
  }
  return fitted;
}
